import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'trace_cart_v2';

/**
 * Cart line item shape:
 * {
 *   id: string (uuid),
 *   product_id: string,
 *   product_name: string,
 *   image: string,
 *   quantity: number,
 *   note: string,
 *   unit_price: number,        // pré-calculado (pizza/composição/extras)
 *   details: {                 // opcional, usado pra UI e payload da API
 *     is_pizza: bool,
 *     pizza_flavors: [{product_id, name, price, fraction}],
 *     pizza_size: {label, extra_price},
 *     pizza_border: {label, extra_price} | null,
 *     composition_selections: [{group_name, price_strategy, options:[{label, extra_price}]}],
 *     extra_price: number,
 *   } | null,
 * }
 */

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [coupon, setCoupon] = useState(() => {
    try {
      const raw = localStorage.getItem(`${STORAGE_KEY}_coupon`);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [table, setTable] = useState(() => {
    try {
      const raw = localStorage.getItem(`${STORAGE_KEY}_table`);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [comanda, setComanda] = useState(() => {
    try {
      const raw = localStorage.getItem(`${STORAGE_KEY}_comanda`);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY}_coupon`, JSON.stringify(coupon));
  }, [coupon]);

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY}_table`, JSON.stringify(table));
  }, [table]);

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY}_comanda`, JSON.stringify(comanda));
  }, [comanda]);

  /** Adiciona um item "rico" já pronto (vindo do Pizza/Composition picker). */
  const addLine = (line) => {
    setItems((prev) => [
      ...prev,
      { id: `${line.product_id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, ...line },
    ]);
  };

  /** Adiciona produto simples (sem composição) — agrupa duplicatas. */
  const addSimple = (product, quantity = 1, note = '') => {
    setItems((prev) => {
      const existing = prev.find(
        (i) => i.product_id === product.id && (i.note || '') === note && !i.details,
      );
      if (existing) {
        return prev.map((i) =>
          i === existing ? { ...i, quantity: i.quantity + quantity } : i,
        );
      }
      return [
        ...prev,
        {
          id: `${product.id}-${Date.now()}`,
          product_id: product.id,
          product_name: product.name,
          image: product.image || '',
          quantity,
          note,
          unit_price: Number(product.price || 0),
          details: null,
        },
      ];
    });
  };

  const updateQuantity = (id, quantity) => {
    setItems((prev) =>
      prev
        .map((i) => (i.id === id ? { ...i, quantity } : i))
        .filter((i) => i.quantity > 0),
    );
  };

  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const clearCart = () => {
    setItems([]);
    setCoupon(null);
  };
  const applyCoupon = (c) => setCoupon(c);
  const setTableInfo = useCallback((t) => setTable(t), []);
  const setComandaInfo = useCallback((c) => setComanda(c), []);

  const subtotal = useMemo(
    () => items.reduce((acc, i) => acc + Number(i.unit_price || 0) * i.quantity, 0),
    [items],
  );

  const couponDiscount = useMemo(() => {
    if (!coupon || !coupon.valid) return 0;
    return Number(coupon.discount || 0);
  }, [coupon]);

  const totalItems = useMemo(() => items.reduce((a, i) => a + i.quantity, 0), [items]);

  const value = {
    items,
    isOpen,
    setIsOpen,
    addLine,
    addSimple,
    updateQuantity,
    removeItem,
    clearCart,
    applyCoupon,
    setTableInfo,
    setComandaInfo,
    coupon,
    couponDiscount,
    table,
    comanda,
    subtotal,
    totalItems,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
